import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/AuthContext";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Loader2, Eye, EyeOff, Check, ArrowRight, Globe, Mail, ArrowLeft, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { RotalieLogo } from "../components/RotalieLogo";

// Step indicator component - moved outside to avoid re-renders
const StepIndicator = ({ step }) => (
  <div className="flex items-center justify-center gap-2 mb-8">
    {[1, 2, 3].map((s) => (
      <div key={s} className="flex items-center">
        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-colors ${
          step >= s 
            ? "bg-gradient-to-r from-violet-500 to-teal-400 text-white" 
            : "bg-slate-200 text-slate-500"
        }`}>
          {step > s ? <Check className="w-4 h-4" /> : s}
        </div>
        {s < 3 && (
          <div className={`w-12 h-0.5 mx-1 ${step > s ? "bg-gradient-to-r from-violet-500 to-teal-400" : "bg-slate-200"}`} />
        )}
      </div>
    ))}
  </div>
);

const Signup = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [countries, setCountries] = useState([]);
  const [detectingLocation, setDetectingLocation] = useState(true);
  
  // Form step: 1 = details, 2 = verify email, 3 = password
  const [step, setStep] = useState(1);
  
  // Form fields
  const [companyName, setCompanyName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [countryCode, setCountryCode] = useState("GB");
  
  // Verification
  const [verificationCode, setVerificationCode] = useState(["", "", "", "", "", ""]);
  const [sendingCode, setSendingCode] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const codeInputRefs = useRef([]);

  const benefits = [
    "Unlimited employees & shifts",
    "Real-time cost tracking",
    "Drag & drop scheduling",
    "Break & absence management",
    "Export reports (CSV/PDF)",
    "Cancel anytime"
  ];

  // Fetch countries and detect user location on mount
  useEffect(() => {
    const init = async () => {
      try {
        const countriesRes = await api.get("/countries");
        setCountries(countriesRes.data.countries || []);
        
        const geoRes = await api.get("/geolocation");
        if (geoRes.data.country_code) {
          setCountryCode(geoRes.data.country_code);
        }
      } catch (error) {
        console.error("Error initializing:", error);
      } finally {
        setDetectingLocation(false);
      }
    };
    init();
  }, []);

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  // Handle sending verification code
  const handleSendVerificationCode = async () => {
    if (!companyName.trim()) {
      toast.error("Please enter your company name");
      return;
    }
    if (!email.trim()) {
      toast.error("Please enter your email");
      return;
    }

    setSendingCode(true);
    try {
      await api.post("/auth/send-verification", {
        email: email.toLowerCase(),
        company_name: companyName
      });
      toast.success("Verification code sent to your email!");
      setStep(2);
      setResendCooldown(60);
      // Focus first code input
      setTimeout(() => codeInputRefs.current[0]?.focus(), 100);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send verification code");
    } finally {
      setSendingCode(false);
    }
  };

  // Handle resending verification code
  const handleResendCode = async () => {
    if (resendCooldown > 0) return;
    
    setSendingCode(true);
    try {
      await api.post("/auth/resend-verification", {
        email: email.toLowerCase(),
        company_name: companyName
      });
      toast.success("New verification code sent!");
      setResendCooldown(60);
      setVerificationCode(["", "", "", "", "", ""]);
      codeInputRefs.current[0]?.focus();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to resend code");
    } finally {
      setSendingCode(false);
    }
  };

  // Handle verification code input
  const handleCodeChange = (index, value) => {
    if (!/^\d*$/.test(value)) return; // Only allow digits
    
    const newCode = [...verificationCode];
    newCode[index] = value.slice(-1); // Only take last character
    setVerificationCode(newCode);

    // Auto-focus next input
    if (value && index < 5) {
      codeInputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all 6 digits entered
    if (newCode.every(d => d) && newCode.join("").length === 6) {
      handleVerifyCode(newCode.join(""));
    }
  };

  // Handle backspace in code input
  const handleCodeKeyDown = (index, e) => {
    if (e.key === "Backspace" && !verificationCode[index] && index > 0) {
      codeInputRefs.current[index - 1]?.focus();
    }
  };

  // Handle paste in code input
  const handleCodePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (pastedData.length === 6) {
      const newCode = pastedData.split("");
      setVerificationCode(newCode);
      handleVerifyCode(pastedData);
    }
  };

  // Verify the code
  const handleVerifyCode = async (code) => {
    setVerifying(true);
    try {
      await api.post("/auth/verify-code", {
        email: email.toLowerCase(),
        code: code
      });
      toast.success("Email verified!");
      setStep(3);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid verification code");
      setVerificationCode(["", "", "", "", "", ""]);
      codeInputRefs.current[0]?.focus();
    } finally {
      setVerifying(false);
    }
  };

  // Final registration
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setLoading(true);
    try {
      await register(companyName, email, password, countryCode);
      toast.success("Account created! Welcome to Rotalie.");
      navigate("/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create account");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col lg:flex-row">
      {/* Left Side - Value Proposition */}
      <div className="hidden lg:flex lg:w-1/2 p-8 xl:p-16 items-center justify-center">
        <div className="w-full max-w-xl">
          {/* Logo */}
          <Link to="/" className="inline-flex items-center gap-3 mb-16">
            <RotalieLogo size="large" />
          </Link>

          {/* Main Value Card */}
          <div className="bg-white rounded-3xl border border-slate-200 shadow-lg p-10 xl:p-12">
            {/* Trial Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-50 border border-amber-200 text-amber-700 font-semibold mb-8">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-500"></span>
              </span>
              30 days free — no card required
            </div>

            <h2 className="text-4xl xl:text-5xl font-bold text-slate-900 font-['Inter'] leading-tight mb-6">
              Stop the chaos.
              <br />
              <span className="bg-gradient-to-r from-violet-600 to-teal-500 bg-clip-text text-transparent">Start scheduling.</span>
            </h2>
            
            <p className="text-xl text-slate-500 leading-relaxed mb-10">
              Join 500+ businesses who ditched spreadsheets and now build rotas in minutes, not hours.
            </p>

            {/* Benefits List */}
            <div className="space-y-4">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-center gap-4">
                  <div className="w-6 h-6 rounded-full bg-gradient-to-r from-violet-500 to-teal-400 flex items-center justify-center flex-shrink-0">
                    <Check className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-lg text-slate-700">{benefit}</span>
                </div>
              ))}
            </div>
          </div>

          <p className="text-slate-400 text-sm mt-10">
            © {new Date().getFullYear()} Rotalie. All rights reserved.
          </p>
        </div>
      </div>

      {/* Right Side - Signup Form */}
      <div className="flex-1 flex flex-col lg:bg-white lg:border-l lg:border-slate-200">
        {/* Mobile Header */}
        <header className="lg:hidden p-6">
          <Link to="/">
            <RotalieLogo />
          </Link>
        </header>

        <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
          <div className="w-full max-w-md">
            <StepIndicator step={step} />

            {/* Step 1: Company Details */}
            {step === 1 && (
              <>
                <div className="mb-8 text-center">
                  <h1 className="text-3xl font-bold text-slate-900 font-['Manrope'] mb-2">Create your account</h1>
                  <p className="text-slate-500">Start your 30-day free trial today</p>
                </div>

                <div className="space-y-5">
                  <div className="space-y-2">
                    <Label htmlFor="companyName" className="text-slate-700 font-medium">Company Name</Label>
                    <Input
                      id="companyName"
                      type="text"
                      placeholder="Your Company Ltd"
                      value={companyName}
                      onChange={(e) => setCompanyName(e.target.value)}
                      required
                      className="h-12 bg-slate-50 border-slate-200 focus:bg-white focus:border-violet-500 focus:ring-violet-500 rounded-xl transition-colors"
                      data-testid="company-name-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="country" className="text-slate-700 font-medium flex items-center gap-2">
                      <Globe className="w-4 h-4" />
                      Country
                      {detectingLocation && (
                        <span className="text-xs text-slate-400 font-normal">(detecting...)</span>
                      )}
                    </Label>
                    <Select value={countryCode} onValueChange={setCountryCode}>
                      <SelectTrigger 
                        className="h-12 bg-slate-50 border-slate-200 focus:bg-white focus:border-violet-500 focus:ring-violet-500 rounded-xl transition-colors"
                        data-testid="country-select"
                      >
                        <SelectValue placeholder="Select your country" />
                      </SelectTrigger>
                      <SelectContent>
                        {countries.map((country) => (
                          <SelectItem key={country.code} value={country.code}>
                            {country.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-400">
                      This determines your billing currency
                    </p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-slate-700 font-medium">Work Email</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@company.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="h-12 bg-slate-50 border-slate-200 focus:bg-white focus:border-violet-500 focus:ring-violet-500 rounded-xl transition-colors"
                      data-testid="signup-email-input"
                    />
                  </div>
                  
                  <Button
                    onClick={handleSendVerificationCode}
                    className="w-full h-14 bg-gradient-to-r from-violet-500 to-teal-400 hover:from-violet-600 hover:to-teal-500 text-white rounded-xl font-semibold text-lg transition-all duration-200 shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40"
                    disabled={sendingCode}
                    data-testid="continue-button"
                  >
                    {sendingCode ? (
                      <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Sending code...</>
                    ) : (
                      <>Continue<ArrowRight className="w-5 h-5 ml-2" /></>
                    )}
                  </Button>

                  <p className="text-center text-slate-400 text-sm">
                    By signing up, you agree to our Terms of Service and Privacy Policy
                  </p>
                </div>
              </>
            )}

            {/* Step 2: Email Verification */}
            {step === 2 && (
              <>
                <div className="mb-8 text-center">
                  <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-4">
                    <Mail className="w-8 h-8 text-violet-600" />
                  </div>
                  <h1 className="text-3xl font-bold text-slate-900 font-['Manrope'] mb-2">Check your email</h1>
                  <p className="text-slate-500">
                    We sent a 6-digit code to<br />
                    <span className="font-semibold text-slate-700">{email}</span>
                  </p>
                </div>

                <div className="space-y-6">
                  {/* 6-digit code input */}
                  <div className="flex justify-center gap-2">
                    {verificationCode.map((digit, index) => (
                      <Input
                        key={index}
                        ref={(el) => (codeInputRefs.current[index] = el)}
                        type="text"
                        inputMode="numeric"
                        maxLength={1}
                        value={digit}
                        onChange={(e) => handleCodeChange(index, e.target.value)}
                        onKeyDown={(e) => handleCodeKeyDown(index, e)}
                        onPaste={handleCodePaste}
                        className="w-12 h-14 text-center text-2xl font-bold bg-slate-50 border-slate-200 focus:bg-white focus:border-violet-500 focus:ring-violet-500 rounded-xl transition-colors"
                        data-testid={`code-input-${index}`}
                        disabled={verifying}
                      />
                    ))}
                  </div>

                  {verifying && (
                    <div className="flex items-center justify-center gap-2 text-violet-600">
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span>Verifying...</span>
                    </div>
                  )}

                  {/* Resend code */}
                  <div className="text-center">
                    <p className="text-slate-500 text-sm mb-2">Did not receive the code?</p>
                    <Button
                      variant="ghost"
                      onClick={handleResendCode}
                      disabled={resendCooldown > 0 || sendingCode}
                      className="text-violet-600 hover:text-violet-700"
                      data-testid="resend-code-button"
                    >
                      {sendingCode ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending...</>
                      ) : resendCooldown > 0 ? (
                        <>Resend in {resendCooldown}s</>
                      ) : (
                        <><RefreshCw className="w-4 h-4 mr-2" />Resend code</>
                      )}
                    </Button>
                  </div>

                  {/* Back button */}
                  <Button
                    variant="outline"
                    onClick={() => setStep(1)}
                    className="w-full h-12 rounded-xl"
                    data-testid="back-button"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Change email
                  </Button>
                </div>
              </>
            )}

            {/* Step 3: Set Password */}
            {step === 3 && (
              <>
                <div className="mb-8 text-center">
                  <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                    <Check className="w-8 h-8 text-emerald-600" />
                  </div>
                  <h1 className="text-3xl font-bold text-slate-900 font-['Manrope'] mb-2">Email verified!</h1>
                  <p className="text-slate-500">Now create a password for your account</p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="space-y-2">
                    <Label htmlFor="password" className="text-slate-700 font-medium">Password</Label>
                    <div className="relative">
                      <Input
                        id="password"
                        type={showPassword ? "text" : "password"}
                        placeholder="Create a strong password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        minLength={6}
                        className="h-12 bg-slate-50 border-slate-200 focus:bg-white focus:border-violet-500 focus:ring-violet-500 pr-12 rounded-xl transition-colors"
                        data-testid="signup-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                      >
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                    <p className="text-xs text-slate-400">Must be at least 6 characters</p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword" className="text-slate-700 font-medium">Confirm Password</Label>
                    <Input
                      id="confirmPassword"
                      type={showPassword ? "text" : "password"}
                      placeholder="Confirm your password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      className="h-12 bg-slate-50 border-slate-200 focus:bg-white focus:border-violet-500 focus:ring-violet-500 rounded-xl transition-colors"
                      data-testid="signup-confirm-password-input"
                    />
                  </div>
                  
                  <Button
                    type="submit"
                    className="w-full h-14 bg-gradient-to-r from-violet-500 to-teal-400 hover:from-violet-600 hover:to-teal-500 text-white rounded-xl font-semibold text-lg transition-all duration-200 shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40"
                    disabled={loading}
                    data-testid="signup-button"
                  >
                    {loading ? (
                      <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Creating account...</>
                    ) : (
                      <>Create Account<ArrowRight className="w-5 h-5 ml-2" /></>
                    )}
                  </Button>
                </form>
              </>
            )}

            <p className="text-center text-slate-500 mt-8">
              Already have an account?{" "}
              <Link to="/login" className="text-violet-600 hover:text-violet-700 font-semibold transition-colors" data-testid="login-link">
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Signup;
